/**
 * The service answers `200` even for a video it has never indexed — an "estimate" row with every
 * count zero, `rawLikes`/`rawDislikes` both `null` (measured 2026-08-03: a made-up 11-character id
 * gets exactly this shape, `deleted: false` included). That is the real "no data" sentinel; the
 * `data-source` response header that also carries it (`estimate` vs `db`) is not CORS-exposed
 * (no `Access-Control-Expose-Headers`), so a browser fetch cannot read it — the body fields are the
 * only signal available at this layer.
 */

export interface ReturnYoutubeDislikeVotes {
  /** The confidence-adjusted estimate, or `null` when the service has nothing on this video. */
  readonly dislikeCount: number | null;
}

const NOTHING: ReturnYoutubeDislikeVotes = { dislikeCount: null };

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function parseReturnYoutubeDislikeVotes(payload: unknown): ReturnYoutubeDislikeVotes {
  if (!isRecord(payload)) return NOTHING;
  if (payload['deleted'] === true) return NOTHING;
  // Both raw fields are `null` together on the "estimate" sentinel; either alone is enough to tell.
  if (payload['rawDislikes'] === null || payload['rawLikes'] === null) return NOTHING;

  const dislikes = payload['dislikes'];
  if (typeof dislikes !== 'number' || !Number.isFinite(dislikes) || dislikes < 0) return NOTHING;
  return { dislikeCount: Math.round(dislikes) };
}
